import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useManagerPermissions } from '../../../hooks/useManagerPermissions';
import { styleSheet } from '../styles';

interface IProps {
	phoneCall: Api.IPhoneCall;
}

const STEP_LABELS: Record<Api.CoachingSuggestionStepName, string> = {
	GetToDm: 'Get to DM',
	Hook: 'Hook',
	Pitch: 'Pitch',
	QualifyingQuestions: 'Qualifying Questions',
	Close: 'Close',
	Other: 'Other',
};

const CLASSIFICATION_LABELS: Record<Api.CoachingClassification, string> = {
	followed: 'Followed',
	not_followed: 'Not followed',
	not_applicable: 'N/A',
};

const CLASSIFICATION_STYLE_KEYS: Record<Api.CoachingClassification, keyof typeof styleSheet> = {
	followed: 'adherenceScorecardPillFollowed',
	not_followed: 'adherenceScorecardPillNotFollowed',
	not_applicable: 'adherenceScorecardPillNotApplicable',
};

const suggestionIcon = (c: Api.CoachingClassification) => {
	if (c === 'followed') return '✓';
	if (c === 'not_followed') return '✗';
	return '—';
};

export const CoachingAdherenceScorecard: React.FC<IProps> = ({ phoneCall }) => {
	const { hasManagerPermissions } = useManagerPermissions();
	if (!hasManagerPermissions) return null;

	const result = phoneCall.coachingAdherenceResult;
	if (!result) return null;

	const evaluatedDate = result.evaluatedDate ? moment(result.evaluatedDate).format('MMM D, YYYY') : null;

	return (
		<section aria-label='Coaching adherence' className={css(styleSheet.adherenceScorecardCard)}>
			<header className={css(styleSheet.adherenceScorecardHeader)}>
				<div className={css(styleSheet.adherenceScorecardScore)}>
					Score: <span className={css(styleSheet.adherenceScorecardScoreValue)}>{result.score}%</span>
				</div>
				{evaluatedDate && (
					<div className={css(styleSheet.adherenceScorecardEvaluatedDate)}>Evaluated {evaluatedDate}</div>
				)}
			</header>

			{result.stepEvaluations.length > 0 && (
				<div className={css(styleSheet.adherenceScorecardStepRow)}>
					{result.stepEvaluations.map(e => (
						<div key={e.step} className={css(styleSheet.adherenceScorecardStepCell)}>
							<div className={css(styleSheet.adherenceScorecardStepLabel)}>{STEP_LABELS[e.step]}</div>
							<span
								className={css(
									styleSheet.adherenceScorecardPill,
									styleSheet[CLASSIFICATION_STYLE_KEYS[e.classification]]
								)}
							>
								{CLASSIFICATION_LABELS[e.classification]}
							</span>
						</div>
					))}
				</div>
			)}

			{result.summary && (
				<div className={css(styleSheet.adherenceScorecardSection)}>
					<h3 className={css(styleSheet.adherenceScorecardSectionTitle)}>Summary</h3>
					<p className={css(styleSheet.adherenceScorecardSummary)}>{result.summary}</p>
				</div>
			)}

			{result.suggestionEvaluations.length > 0 && (
				<div className={css(styleSheet.adherenceScorecardSection)}>
					<h3 className={css(styleSheet.adherenceScorecardSectionTitle)}>Coaching feedback</h3>
					<ul className={css(styleSheet.adherenceScorecardSuggestionList)}>
						{result.suggestionEvaluations.map((s, i) => (
							<li key={`${s.step}-${i}`} className={css(styleSheet.adherenceScorecardSuggestionItem)}>
								<span
									className={css(
										styleSheet.adherenceScorecardSuggestionIcon,
										styleSheet[CLASSIFICATION_STYLE_KEYS[s.classification]]
									)}
								>
									{suggestionIcon(s.classification)}
								</span>
								<div className={css(styleSheet.adherenceScorecardSuggestionBody)}>
									<span className={css(styleSheet.adherenceScorecardSuggestionStep)}>{STEP_LABELS[s.step]}</span>
									<span className={css(styleSheet.adherenceScorecardSuggestionReasoning)}>{s.reasoning}</span>
								</div>
							</li>
						))}
					</ul>
				</div>
			)}
		</section>
	);
};
